import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Check, Crown } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Pricing() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState("monthly");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data));
  }, []);

  const subscribe = async (planId) => {
    if (!user || typeof user !== "object") {
      toast.error("Sign in to activate a membership");
      nav("/login");
      return;
    }
    setBusy(planId);
    try {
      await api.post("/subscribe", { plan_id: planId, billing });
      toast.success("Membership activated (demo)");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not activate");
    } finally {
      setBusy("");
    }
  };

  return (
    <section id="pricing" data-testid="subscription-plans-section" className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="eyebrow">Membership</div>
            <h2 className="font-display text-4xl sm:text-5xl uppercase mt-2 leading-tight">
              Pick your <span className="gold-text">lane</span>
            </h2>
            <p className="text-[var(--text-secondary)] mt-3 max-w-xl">
              Every tier is a full-access ecosystem. Upgrade anytime. Cancel anytime.
            </p>
          </div>
          <div className="inline-flex rounded-full border border-[var(--border-subtle)] p-1">
            {["monthly", "annual"].map((b) => (
              <button
                key={b}
                data-testid={`billing-toggle-${b}`}
                onClick={() => setBilling(b)}
                className={`px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest ${
                  billing === b
                    ? "bg-[var(--gold)] text-[#0a0a0e]"
                    : "text-[var(--text-secondary)]"
                }`}
              >
                {b}{b === "annual" ? " · save 15%" : ""}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {plans.map((p) => {
            const featured = p.featured;
            const price = billing === "monthly" ? p.price_monthly : Math.round(p.price_annual / 12);
            return (
              <div
                key={p.id}
                data-testid={`pricing-plan-${p.id}`}
                className={`gr-card p-7 flex flex-col ${
                  featured ? "border-[var(--border-gold)] shadow-[0_0_40px_-15px_rgba(212,175,55,0.4)]" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="eyebrow">{p.tag}</div>
                  {featured && (
                    <span className="text-[10px] px-2 py-1 rounded-full bg-[rgba(212,175,55,0.12)] gold-text font-mono uppercase tracking-widest border border-[var(--border-gold)]">
                      Most Chosen
                    </span>
                  )}
                </div>
                <h3 className="font-display text-3xl uppercase mt-3">{p.name}</h3>
                <div className="mt-5 flex items-end gap-1">
                  <span className="font-display text-6xl gold-text">${price}</span>
                  <span className="text-xs text-[var(--text-muted)] uppercase tracking-widest mb-2">
                    /mo{billing === "annual" ? " · billed yearly" : ""}
                  </span>
                </div>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.perks.map((perk) => (
                    <li key={perk} className="flex gap-2 text-sm text-[var(--text-secondary)]">
                      <Check size={16} className="text-[var(--gold)] flex-shrink-0 mt-0.5" />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`subscribe-btn-${p.id}`}
                  onClick={() => subscribe(p.id)}
                  disabled={busy === p.id}
                  className={`mt-6 py-3 rounded-full text-sm ${
                    featured ? "gr-btn-gold" : "gr-btn-ghost"
                  } inline-flex items-center justify-center gap-2 disabled:opacity-60`}
                >
                  {p.id === "don" && <Crown size={14} />} {p.cta}
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-[var(--text-muted)] mt-4">
          Payments intentionally skipped in this build — subscription activation is DEMO for now.
        </p>
      </div>
    </section>
  );
}
